// Public listing landing page — NO AUTH, NO Layout (no sidebar/top bar).
// SEO-friendly, AR/EN toggle, photo gallery placeholder, lead-capture form
// that feeds the authenticated team's Prospects pipeline.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { leasingApi } from '@/utils/leasingApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bed, Bath, Maximize2, MapPin, Globe, CheckCircle2, ArrowLeft, Mail, Phone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PublicListing() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('en');
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [error_msg, setErrorMsg] = useState('');

  useEffect(() => {
    leasingApi.publicListing(slug)
      .then(r => {
        setListing(r.data);
        if (!r.data.title_en && r.data.title_ar) setLang('ar');
        document.title = `${r.data.title_en || r.data.title_ar} — REVA`;
        const m = document.querySelector('meta[name="description"]') || (() => {
          const el = document.createElement('meta'); el.name = 'description'; document.head.appendChild(el); return el;
        })();
        m.content = (r.data.description_en || r.data.description_ar || '').slice(0, 160);
      })
      .catch(e => setError(e?.response?.status === 404 ? 'Listing not found or not published yet.' : 'Failed to load listing'))
      .finally(() => setLoading(false));
  }, [slug]);

  const isAr = lang === 'ar';
  const t = (en, ar) => isAr ? ar : en;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.full_name.trim() || !form.phone.trim()) {
      setErrorMsg(t('Name and phone are required.', 'الاسم ورقم الهاتف مطلوبان.'));
      return;
    }
    setSubmitting(true);
    try {
      await leasingApi.publicLead(slug, form);
      setSubmitted(true);
    } catch (err) {
      setErrorMsg(t('Submission failed. Please try again.', 'فشل الإرسال. حاول مرّة أخرى.'));
    }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FBFCFD]"><Loader2 className="animate-spin h-6 w-6 text-[#00734A]" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBFCFD]" data-testid="public-listing-error">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold text-[#0E1320]">{error}</h1>
        <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-[#00734A] hover:underline"><ArrowLeft size={14} /> Back to home</Link>
      </div>
    </div>
  );

  const title = isAr && listing.title_ar ? listing.title_ar : listing.title_en;
  const desc  = isAr && listing.description_ar ? listing.description_ar : listing.description_en;
  const hasBothLangs = listing.title_ar && listing.title_en;

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="min-h-screen bg-[#FBFCFD] text-[#0E1320]" data-testid="public-listing-page">
      {/* Header */}
      <header className="border-b border-[#E6EAF0] bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="font-heading font-bold text-lg tracking-tight">REVA</Link>
          <div className="flex items-center gap-2">
            {hasBothLangs && (
              <button onClick={() => setLang(isAr ? 'en' : 'ar')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium reva-mono border border-[#E6EAF0] rounded-lg hover:border-[#0E1320]" data-testid="public-listing-lang">
                <Globe size={12} /> {isAr ? 'EN' : 'AR'}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8 grid lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Hero image placeholder */}
          <div className="aspect-[16/9] rounded-2xl bg-gradient-to-br from-[#0E1320] via-[#1a2030] to-[#2a3548] flex items-center justify-center relative overflow-hidden border border-[#E6EAF0]">
            {listing.photos?.length > 0 ? (
              <img src={listing.photos[0]} alt={title} className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-white/40">
                <Maximize2 className="w-12 h-12 mx-auto mb-2" />
                <div className="text-xs reva-mono uppercase tracking-[0.2em]">Photo gallery coming soon</div>
              </div>
            )}
            {listing.district && (
              <div className="absolute bottom-4 start-4 px-3 py-1.5 bg-black/70 backdrop-blur rounded-lg text-white text-[12px] font-medium inline-flex items-center gap-1.5">
                <MapPin size={12} /> {listing.district}{listing.city ? `, ${listing.city}` : ''}
              </div>
            )}
          </div>

          {/* Title + price */}
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" data-testid="public-listing-title">{title}</h1>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-3xl font-bold text-[#00734A] tabular-nums">SAR {listing.monthly_rent_sar.toLocaleString()}</span>
              <span className="text-[#71777E]">/ {t('month', 'شهر')}</span>
            </div>
          </div>

          {/* Specs */}
          <div className="grid grid-cols-3 gap-3 py-4 border-y border-[#ECEFF3]">
            <Spec icon={Bed} label={t('Bedrooms', 'غرف')} value={listing.bedrooms} />
            <Spec icon={Bath} label={t('Bathrooms', 'حمامات')} value={listing.bathrooms} />
            <Spec icon={Maximize2} label={t('Size', 'المساحة')} value={`${listing.size_sqm || '—'} sqm`} />
          </div>

          {/* Description */}
          {desc && (
            <div>
              <h2 className="text-lg font-semibold mb-2">{t('About this property', 'عن هذا العقار')}</h2>
              <p className="text-[14px] leading-relaxed text-[#4A5263] whitespace-pre-line">{desc}</p>
            </div>
          )}

          {/* Amenities */}
          {listing.amenities?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3">{t('Amenities', 'المرافق')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {listing.amenities.map(a => (
                  <div key={a} className="inline-flex items-center gap-2 text-[13px] text-[#0E1320] py-1.5">
                    <CheckCircle2 size={14} className="text-[#00734A]" /> <span className="capitalize">{a.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lead capture sidebar */}
        <aside className="lg:col-span-1">
          <div className="sticky top-20 rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-sm" data-testid="public-listing-lead-form">
            {submitted ? (
              <div className="text-center py-6" data-testid="public-listing-submitted">
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="mt-3 text-lg font-semibold">{t('Thanks!', 'شكرًا لك')}</h3>
                <p className="mt-1 text-[13px] text-[#4A5263]">{t('The leasing team will reach out within 24h.', 'سيتواصل معك فريق التأجير خلال 24 ساعة.')}</p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold">{t('Interested? Get in touch.', 'مهتم؟ تواصل معنا.')}</h3>
                <p className="mt-1 text-[12.5px] text-[#71777E]">{t('Reach out and we will arrange a viewing.', 'تواصل معنا لترتيب معاينة.')}</p>
                <form onSubmit={onSubmit} className="mt-4 space-y-3" noValidate>
                  <Input placeholder={t('Full name', 'الاسم الكامل')} value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="public-lead-name" />
                  <Input type="tel" placeholder={t('Phone number', 'رقم الهاتف')} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="public-lead-phone" />
                  <Input type="email" placeholder={t('Email (optional)', 'بريد إلكتروني (اختياري)')} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="public-lead-email" />
                  <textarea
                    placeholder={t('Message (optional)', 'رسالة (اختياري)')}
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-[#E6EAF0] rounded-md text-sm focus:border-[#00734A] focus:outline-none"
                    data-testid="public-lead-message"
                  />
                  {error_msg && (
                    <div role="alert" className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-[12px] text-red-700" data-testid="public-lead-error">
                      {error_msg}
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-[#00734A] hover:bg-[#005f3d]" disabled={submitting} data-testid="public-lead-submit">
                    {submitting ? <><Loader2 className="animate-spin h-4 w-4 me-2" /> {t('Sending…', 'إرسال…')}</> : t('Request information', 'طلب معلومات')}
                  </Button>
                  <div className="text-[10.5px] text-[#71777E] reva-mono uppercase tracking-[0.14em] text-center">
                    {t('Powered by REVA', 'مدعوم من ريفا')}
                  </div>
                </form>
              </>
            )}
          </div>
        </aside>
      </div>

      <footer className="border-t border-[#E6EAF0] mt-12">
        <div className="max-w-6xl mx-auto px-5 py-6 text-center text-[12px] text-[#71777E]">
          © {new Date().getFullYear()} REVA · {t('Operational intelligence for KSA real estate.', 'الذكاء التشغيلي للعقارات السعوديّة.')}
        </div>
      </footer>
    </div>
  );
}

const Spec = ({ icon: Icon, label, value }) => (
  <div className="text-center">
    <Icon className="mx-auto h-4 w-4 text-[#4A5263]" />
    <div className="mt-1.5 text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">{label}</div>
    <div className="text-lg font-semibold tabular-nums">{value || '—'}</div>
  </div>
);
